import React, { useState, useEffect } from "react";
import {
  ArrowLeft,
  Image as ImageIcon,
  Loader2,
  X,
  Upload,
  Eye,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useLandlordStore } from "@/stores/useLandlordStore";
import { LoadingScreen } from "@/components/common/LoadingSpinner";
import apiClient from "@/services/apiClient";
import { useToast } from "@/contexts/ToastContext";

export const PostVacancy: React.FC = () => {
  const navigate = useNavigate();
  const { properties, fetchProperties, createVacancy } = useLandlordStore();
  const { warning, success, error: toastError, info } = useToast();
  const [selectedPropertyId, setSelectedPropertyId] = useState<string>("");
  const [availableUnits, setAvailableUnits] = useState<any[]>([]);
  const [selectedUnitId, setSelectedUnitId] = useState<string>("");
  
  const [postTitle, setPostTitle] = useState("");
  const [description, setDescription] = useState("");
  const [images, setImages] = useState<string[]>([]);
  
  const [isLoadingProps, setIsLoadingProps] = useState(false);
  const [isLoadingUnits, setIsLoadingUnits] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);

  useEffect(() => {
    if (properties.length === 0) {
      setIsLoadingProps(true);
      fetchProperties().finally(() => setIsLoadingProps(false));
    }
  }, [properties.length, fetchProperties]);

  const handlePropertyChange = async (propertyId: string) => {
    setSelectedPropertyId(propertyId);
    setSelectedUnitId("");
    setPostTitle("");
    setAvailableUnits([]);
    
    if (!propertyId) return;

    setIsLoadingUnits(true);
    try {
      const response = await apiClient.get(`/units/property/${propertyId}`);
      const units = response.data.filter((u: any) => u.status === "Available");
      setAvailableUnits(units);
    } catch (error) {
      console.error("Could not fetch units:", error);
      toastError('Failed to load units', 'Error fetching units for this property.');
    } finally {
      setIsLoadingUnits(false);
    }
  };

  const handleUnitChange = (unitId: string) => {
    setSelectedUnitId(unitId);
    const currProp = properties.find((p) => p.id === selectedPropertyId);
    const currUnit = availableUnits.find((u) => u.id === unitId);
    if (currProp && currUnit) {
      setPostTitle(`${currUnit.unit_number} in ${currProp.name}`);
    } else {
      setPostTitle("");
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newImages = Array.from(e.target.files).map((f) =>
        URL.createObjectURL(f)
      );
      setImages((prev) => [...prev, ...newImages]);
    }
  };

  const removeImage = (uri: string) => {
    setImages(images.filter((image) => image !== uri));
  };

  const handlePreview = () => {
    if (!selectedPropertyId || !selectedUnitId || !postTitle) {
      warning('Incomplete form', 'Please select a property, unit, and add a title before previewing.');
      return;
    }

    const selectedUnit = availableUnits.find(u => u.id === selectedUnitId);
    const previewData = {
      property_id: selectedPropertyId,
      unit_id: selectedUnitId,
      title: postTitle,
      description,
      rent_amount: selectedUnit?.rent_amount,
      images,
      property_name: properties.find(p => p.id === selectedPropertyId)?.name,
      unit_name: selectedUnit?.unit_number,
      bedrooms: (selectedUnit as any)?.bedrooms,
      bathrooms: (selectedUnit as any)?.bathrooms,
      amenities: (selectedUnit as any)?.amenities,
      isEdit: false,
    };

    navigate('/landlord/vacancies/preview', { state: { vacancy: previewData } });
  };

  const handlePublish = async () => {
    if (!selectedPropertyId || !selectedUnitId || !postTitle || images.length === 0) {
      warning('Incomplete form', 'Please select a property, unit, title, and at least one image.');
      return;
    }

    const selectedUnit = availableUnits.find(u => u.id === selectedUnitId);
    if (!selectedUnit) return;

    setIsPublishing(true);
    
    try {
      // Typically we'd upload images first and pass the keys.
      // Mocking the keys using local URLs for now to prevent backend R2 upload blockages on dev env
      const imageKeys = images;

      await createVacancy({
        propertyId: selectedPropertyId,
        unitId: selectedUnitId,
        title: postTitle,
        description,
        rentAmount: selectedUnit.rent_amount,
        imageKeys,
      });

      success('Vacancy published!', 'Your vacancy is now live on the platform.');
      navigate("/landlord/vacancies");
    } catch (error) {
      console.error("Failed to publish vacancy:", error);
      toastError('Publish failed', 'Failed to publish vacancy. See console for details.');
    } finally {
      setIsPublishing(false);
    }
  };

  if (isLoadingProps && properties.length === 0) {
    return <LoadingScreen />;
  }

  const inputClass =
    "w-full px-4 py-3 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-xl text-light-text dark:text-dark-text placeholder:text-light-subtle dark:text-dark-subtle focus:outline-none focus:ring-2 focus:ring-primary/50 transition-shadow";
  const labelClass =
    "block text-sm font-semibold text-light-text dark:text-dark-text mb-2";

  return (
    <div className="max-w-3xl mx-auto pb-12 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4 bg-light-card dark:bg-dark-card p-4 rounded-lg shadow-sm border border-light-border dark:border-dark-border">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-full hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
        >
          <ArrowLeft size={24} className="text-primary dark:text-golden" />
        </button>
        <h1 className="text-xl font-bold text-light-text dark:text-dark-text">
          Create Vacancy
        </h1>
      </div>

      <div className="bg-light-card dark:bg-dark-card p-6 md:p-8 rounded-xl shadow-sm border border-light-border dark:border-dark-border space-y-8">
        {/* Selection Area */}
        <div className="space-y-4">
          <h2 className="text-lg font-bold text-light-text dark:text-dark-text border-b border-light-border dark:border-dark-border pb-2">
            Property & Unit
          </h2>
          <div>
            <label className={labelClass}>Select Property</label>
            <select
              value={selectedPropertyId}
              onChange={(e) => handlePropertyChange(e.target.value)}
              className={inputClass}
            >
              <option value="">-- Choose Property --</option>
              {properties.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelClass}>
              Select Unit{" "}
              {isLoadingUnits && <span className="text-xs text-primary ml-2 italic">Loading...</span>}
            </label>
            <select
              value={selectedUnitId}
              onChange={(e) => handleUnitChange(e.target.value)}
              className={inputClass}
              disabled={!selectedPropertyId || isLoadingUnits || availableUnits.length === 0}
            >
              <option value="">-- Choose Unit --</option>
              {availableUnits.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.unit_number} - KES {u.rent_amount?.toLocaleString() || 0}
                </option>
              ))}
            </select>
            {selectedPropertyId && !isLoadingUnits && availableUnits.length === 0 && (
              <p className="text-sm text-amber-600 mt-2">No available units found at this property.</p>
            )}
          </div>
        </div>

        {/* Input Text Form */}
        <div className="space-y-4">
          <h2 className="text-lg font-bold text-light-text dark:text-dark-text border-b border-light-border dark:border-dark-border pb-2">
            Details
          </h2>
          <div>
            <label className={labelClass}>Vacancy Title</label>
            <input
              type="text"
              value={postTitle}
              onChange={(e) => setPostTitle(e.target.value)}
              placeholder="e.g. Stunning 2BR Apartment"
              className={inputClass}
              required
            />
          </div>

          <div>
            <label className={labelClass}>Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the unit..."
              rows={4}
              className={inputClass}
              required
            />
          </div>
        </div>

        {/* Image Uploader */}
        <div className="space-y-4">
          <h2 className="text-lg font-bold text-light-text dark:text-dark-text border-b border-light-border dark:border-dark-border pb-2">
            Photos
          </h2>
          <div className="flex flex-wrap gap-4">
            {images.map((uri) => (
              <div
                key={uri}
                className="relative w-24 h-24 rounded-lg overflow-hidden border border-light-border dark:border-dark-border"
              >
                <img
                  src={uri}
                  alt="preview"
                  className="w-full h-full object-cover"
                />
                <button
                  type="button"
                  onClick={() => removeImage(uri)}
                  className="absolute top-1 right-1 bg-black/60 rounded-full p-1"
                >
                  <X size={14} className="text-white" />
                </button>
              </div>
            ))}

            <label className="flex flex-col items-center justify-center w-24 h-24 rounded-lg border-2 border-dashed border-light-border dark:border-dark-border cursor-pointer hover:bg-light-bg dark:hover:bg-dark-bg transition-colors">
              <Upload size={24} className="text-light-subtle dark:text-dark-subtle mb-1" />
              <span className="text-[10px] text-light-subtle dark:text-dark-subtle font-medium uppercase text-center leading-tight">Add<br />Images</span>
              <input
                type="file"
                className="hidden"
                accept="image/*"
                multiple
                onChange={handleImageChange}
              />
            </label>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="pt-4 flex gap-4">
          <button
            type="button"
            className="flex-1 py-3 text-center bg-gray-200 dark:bg-gray-800 text-gray-800 dark:text-gray-200 rounded-xl font-bold text-base hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
            onClick={handlePreview}
          >
            <Eye size={20} />
            Preview
          </button>
          <button
            type="button"
            disabled={isPublishing}
            onClick={handlePublish}
            className="flex-1 py-3 text-center bg-primary text-white rounded-xl font-bold text-base hover:opacity-90 disabled:opacity-50 transition-opacity flex items-center justify-center gap-2"
          >
            {isPublishing && <Loader2 className="animate-spin" size={20} />}
            {isPublishing ? "Publishing..." : "Publish"}
          </button>
        </div>
      </div>
    </div>
  );
};
